import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import * as adminApi from '../../api/admin.api';
import { getApiErrorMessage } from '../../utils/apiError';
import type { AdminUser, AdminVPNKeyTraffic } from '../../types/admin';
import styles from './AdminUsers.module.css';

const LIMIT = 20;

const subscriptionVariant: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  expired: 'error',
  cancelled: 'warning',
  pending: 'neutral',
};

const subscriptionLabel: Record<string, string> = {
  active: 'Активна',
  expired: 'Истекла',
  cancelled: 'Отменена',
  pending: 'Ожидание',
  none: 'Нет',
};

const trialLabel: Record<string, string> = {
  active: 'Активен',
  expired: 'Истёк',
  traffic_exceeded: 'Трафик исчерпан',
};

const trialVariant: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  expired: 'neutral',
  traffic_exceeded: 'warning',
};

function formatMB(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} ГБ`;
  return `${mb.toFixed(1)} МБ`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const marzbanStatusLabel: Record<string, string> = {
  active: 'Активен',
  disabled: 'Отключён',
  limited: 'Лимит исчерпан',
  expired: 'Истёк',
  on_hold: 'Ожидание',
};

const marzbanStatusVariant: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  disabled: 'neutral',
  limited: 'warning',
  expired: 'error',
  on_hold: 'neutral',
};

export function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [hwidExemptLoading, setHwidExemptLoading] = useState(false);
  // marzbanUsername ключа, у которого сейчас идёт отвязка устройства
  const [unbindingKey, setUnbindingKey] = useState<string | null>(null);

  const openUserDetails = async (u: AdminUser) => {
    // Open modal immediately with list data, then enrich from GET /admin/users/:id
    setSelectedUser(u);
    setDetailsLoading(true);
    try {
      const full = await adminApi.getUser(u.id);
      setSelectedUser(full);
    } catch {
      // Fall back to list data silently
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminApi.getUsers(page, LIMIT, search || undefined);
      setUsers(result.data);
      setTotal(result.total);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const totalPages = Math.ceil(total / LIMIT);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  const handleToggleRole = async (u: AdminUser) => {
    setActionLoading(true);
    try {
      const newRole = u.role === 'admin' ? 'user' : 'admin';
      await adminApi.updateUser(u.id, { role: newRole });
      await fetchUsers();
      setSelectedUser(null);
    } catch {
      // Silently ignore
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleBlock = async (u: AdminUser) => {
    setActionLoading(true);
    try {
      await adminApi.updateUser(u.id, { isBlocked: !u.isBlocked });
      await fetchUsers();
      setSelectedUser(null);
    } catch {
      // Silently ignore
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleHwidExempt = async (u: AdminUser) => {
    const next = !u.hwidExempt;
    setHwidExemptLoading(true);
    try {
      await adminApi.updateUser(u.id, { hwidExempt: next });
      // Мержим только флаг: ответ PATCH без vpnKeys, карточку терять не хотим
      setSelectedUser((prev) => (prev && prev.id === u.id ? { ...prev, hwidExempt: next } : prev));
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, hwidExempt: next } : x)));
      toast.success(next ? 'Исключение из привязки включено' : 'Исключение из привязки снято');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Не удалось изменить исключение из привязки'));
    } finally {
      setHwidExemptLoading(false);
    }
  };

  const handleUnbindDevice = async (u: AdminUser, k: AdminVPNKeyTraffic) => {
    if (
      !window.confirm(
        'Отвязать устройство у этого ключа? Клиент перестанет получать подписку, пока не вставит ключ заново.'
      )
    ) {
      return;
    }
    setUnbindingKey(k.marzbanUsername);
    try {
      if (k.kind === 'trial') {
        await adminApi.unbindTrialDevice(u.id);
      } else {
        await adminApi.unbindKeyDevice(k.keyId);
      }
      toast.success('Устройство отвязано');
      const full = await adminApi.getUser(u.id);
      setSelectedUser((prev) => (prev && prev.id === u.id ? full : prev));
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Не удалось отвязать устройство'));
    } finally {
      setUnbindingKey(null);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied}>Доступ запрещён</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Пользователи</h1>
        <div className={styles.searchRow}>
          <input
            className={styles.searchInput}
            placeholder="Поиск по email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button variant="secondary" size="sm" onClick={handleSearch}>
            Найти
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spinner size="lg" />
        </div>
      ) : users.length === 0 ? (
        <div className={styles.emptyState}>Пользователи не найдены</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Роль</th>
                <th>Подписка</th>
                <th>Создан</th>
                <th>Ключи</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.email}
                    {u.hwidExempt && (
                      <Badge variant="warning" className={styles.blockedBadge}>
                        искл.
                      </Badge>
                    )}
                    {u.isBlocked && (
                      <Badge variant="error" className={styles.blockedBadge}>
                        Заблокирован
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Badge variant={u.role === 'admin' ? 'info' : 'neutral'}>
                      {u.role === 'admin' ? 'Админ' : 'Пользователь'}
                    </Badge>
                  </td>
                  <td>
                    {u.subscriptionStatus ? (
                      <Badge variant={subscriptionVariant[u.subscriptionStatus] || 'neutral'}>
                        {subscriptionLabel[u.subscriptionStatus] || u.subscriptionStatus}
                      </Badge>
                    ) : (
                      <Badge variant="neutral">Нет</Badge>
                    )}
                  </td>
                  <td>{formatDate(u.createdAt)}</td>
                  <td>{u.keyCount}</td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionBtn}
                        onClick={() => openUserDetails(u)}
                      >
                        Подробнее
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Назад
              </button>
              <span className={styles.pageInfo}>
                {page} из {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Далее
              </button>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        title="Информация о пользователе"
      >
        {selectedUser && (
          <>
            <h3 className={styles.modalTitle}>
              Информация о пользователе
              {detailsLoading && <Spinner size="sm" />}
            </h3>

            {/* Профиль */}
            <div className={styles.modalSection}>
              <div className={styles.modalSectionTitle}>Профиль</div>

              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Email</div>
                <div className={styles.modalValue}>{selectedUser.email}</div>
              </div>

              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Роль</div>
                <div className={styles.modalValue}>
                  <Badge variant={selectedUser.role === 'admin' ? 'info' : 'neutral'}>
                    {selectedUser.role === 'admin' ? 'Администратор' : 'Пользователь'}
                  </Badge>
                </div>
              </div>

              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Telegram</div>
                <div className={styles.modalValue}>
                  <Badge variant={selectedUser.telegramLinked ? 'success' : 'neutral'}>
                    {selectedUser.telegramLinked ? 'Привязан' : 'Не привязан'}
                  </Badge>
                </div>
              </div>

              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Реферальный код</div>
                <div className={styles.modalValue}>
                  <span className={styles.modalMono}>{selectedUser.referralCode || '—'}</span>
                </div>
              </div>

              {selectedUser.referredBy && (
                <div className={styles.modalField}>
                  <div className={styles.modalLabel}>Приглашён пользователем</div>
                  <div className={styles.modalValue}>
                    <span className={styles.modalMono}>{selectedUser.referredBy}</span>
                  </div>
                </div>
              )}

              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Дата регистрации</div>
                <div className={styles.modalValue}>{formatDate(selectedUser.createdAt)}</div>
              </div>

              <div className={styles.modalField}>
                <div className={styles.toggleRow}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={selectedUser.hwidExempt}
                    aria-label="Исключение из привязки устройств"
                    className={`${styles.toggle} ${selectedUser.hwidExempt ? styles.toggleOn : ''}`}
                    disabled={hwidExemptLoading}
                    onClick={() => handleToggleHwidExempt(selectedUser)}
                  >
                    <span className={styles.toggleThumb} />
                  </button>
                  <div>
                    <div className={styles.toggleLabel}>
                      Исключение из привязки устройств
                      {hwidExemptLoading && <Spinner size="sm" />}
                    </div>
                    <div className={styles.toggleHint}>
                      Включено — для этого аккаунта привязка «1 ключ = 1 устройство» и проверка
                      клиента не действуют, подписку получит любое приложение.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Подписка */}
            <div className={styles.modalSection}>
              <div className={styles.modalSectionTitle}>Подписка</div>

              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Статус</div>
                <div className={styles.modalValue}>
                  {selectedUser.subscriptionStatus ? (
                    <Badge variant={subscriptionVariant[selectedUser.subscriptionStatus] || 'neutral'}>
                      {subscriptionLabel[selectedUser.subscriptionStatus] || selectedUser.subscriptionStatus}
                    </Badge>
                  ) : (
                    <Badge variant="neutral">Нет</Badge>
                  )}
                </div>
              </div>

              {selectedUser.subscriptionPlanName && (
                <div className={styles.modalField}>
                  <div className={styles.modalLabel}>Тариф</div>
                  <div className={styles.modalValue}>{selectedUser.subscriptionPlanName}</div>
                </div>
              )}

              {selectedUser.subscriptionStartAt && (
                <div className={styles.modalField}>
                  <div className={styles.modalLabel}>Начало</div>
                  <div className={styles.modalValue}>{selectedUser.subscriptionStartAt}</div>
                </div>
              )}

              {selectedUser.subscriptionExpiresAt && (
                <div className={styles.modalField}>
                  <div className={styles.modalLabel}>Истекает</div>
                  <div className={styles.modalValue}>{selectedUser.subscriptionExpiresAt}</div>
                </div>
              )}
            </div>

            {/* Триал — показываем только если есть */}
            {selectedUser.trialStatus && (
              <div className={styles.modalSection}>
                <div className={styles.modalSectionTitle}>Пробный период</div>

                <div className={styles.modalField}>
                  <div className={styles.modalLabel}>Статус</div>
                  <div className={styles.modalValue}>
                    <Badge variant={trialVariant[selectedUser.trialStatus] || 'neutral'}>
                      {trialLabel[selectedUser.trialStatus] || selectedUser.trialStatus}
                    </Badge>
                  </div>
                </div>

                {selectedUser.trialExpiresAt && (
                  <div className={styles.modalField}>
                    <div className={styles.modalLabel}>Истекает</div>
                    <div className={styles.modalValue}>{selectedUser.trialExpiresAt}</div>
                  </div>
                )}

                {selectedUser.usedMbTrial != null && selectedUser.totalMbTrial != null && (
                  <div className={styles.modalField}>
                    <div className={styles.modalLabel}>Трафик</div>
                    <div className={styles.modalValue}>
                      <div>
                        {formatMB(selectedUser.usedMbTrial)} / {formatMB(selectedUser.totalMbTrial)}
                      </div>
                      {selectedUser.totalMbTrial > 0 && (
                        <>
                          <div className={styles.trafficBar}>
                            <div
                              className={`${styles.trafficFill} ${
                                selectedUser.usedMbTrial / selectedUser.totalMbTrial > 0.8
                                  ? styles.trafficFillWarning
                                  : ''
                              }`}
                              style={{
                                width: `${Math.min(
                                  100,
                                  (selectedUser.usedMbTrial / selectedUser.totalMbTrial) * 100
                                ).toFixed(0)}%`,
                              }}
                            />
                          </div>
                          <div className={styles.trafficText}>
                            {((selectedUser.usedMbTrial / selectedUser.totalMbTrial) * 100).toFixed(1)}% использовано
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {selectedUser.marzbanTrialUserId && (
                  <div className={styles.modalField}>
                    <div className={styles.modalLabel}>Marzban username</div>
                    <div className={styles.modalValue}>
                      <span className={styles.modalMono}>{selectedUser.marzbanTrialUserId}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Трафик и ключи — подгружается при открытии карточки */}
            {detailsLoading && !selectedUser.vpnKeys && (
              <div className={styles.modalSection}>
                <div className={styles.modalSectionTitle}>Трафик и ключи</div>
                <div className={styles.modalValue}>
                  <Spinner size="sm" /> Загрузка статистики из Marzban…
                </div>
              </div>
            )}

            {selectedUser.vpnKeys && selectedUser.vpnKeys.length > 0 && (
              <div className={styles.modalSection}>
                <div className={styles.modalSectionTitle}>Трафик и ключи</div>

                <div className={styles.modalField}>
                  <div className={styles.modalLabel}>Последний онлайн</div>
                  <div className={styles.modalValue}>
                    {selectedUser.lastOnlineAt ? formatDateTime(selectedUser.lastOnlineAt) : 'никогда'}
                  </div>
                </div>

                {selectedUser.totalUsedMb != null && (
                  <div className={styles.modalField}>
                    <div className={styles.modalLabel}>Всего трафика</div>
                    <div className={styles.modalValue}>{formatMB(selectedUser.totalUsedMb)}</div>
                  </div>
                )}

                {selectedUser.vpnKeys.map((k) => (
                  <div key={k.marzbanUsername} className={styles.keyCard}>
                    <div className={styles.keyHeader}>
                      <span className={styles.modalMono}>{k.marzbanUsername}</span>
                      <Badge variant={k.kind === 'trial' ? 'info' : k.isActive ? 'success' : 'neutral'}>
                        {k.kind === 'trial' ? 'Триал' : k.isActive ? 'Активен' : 'Неактивен'}
                      </Badge>
                    </div>

                    {k.deviceName && <div className={styles.keyDevice}>{k.deviceName}</div>}

                    {!k.found ? (
                      <div className={styles.keyMuted}>Отсутствует в Marzban (ключ удалён)</div>
                    ) : (
                      <>
                        <div className={styles.keyRow}>
                          <span>Трафик</span>
                          <span>
                            {formatMB(k.usedMb)}
                            {k.limitMb > 0 ? ` / ${formatMB(k.limitMb)}` : ' (безлимит)'}
                          </span>
                        </div>
                        {k.limitMb > 0 && (
                          <div className={styles.trafficBar}>
                            <div
                              className={`${styles.trafficFill} ${
                                k.usedMb / k.limitMb > 0.8 ? styles.trafficFillWarning : ''
                              }`}
                              style={{ width: `${Math.min(100, (k.usedMb / k.limitMb) * 100).toFixed(0)}%` }}
                            />
                          </div>
                        )}
                        <div className={styles.keyRow}>
                          <span>Онлайн</span>
                          <span>{k.onlineAt ? formatDateTime(k.onlineAt) : 'никогда'}</span>
                        </div>
                        <div className={styles.keyRow}>
                          <span>Статус Marzban</span>
                          <Badge variant={marzbanStatusVariant[k.status] || 'neutral'}>
                            {marzbanStatusLabel[k.status] || k.status || '—'}
                          </Badge>
                        </div>

                        <div className={styles.deviceSection}>
                          <div className={styles.deviceTitle}>Привязка</div>
                          {k.device ? (
                            <div className={styles.deviceCard}>
                              <div className={styles.deviceName}>{k.device.title || 'Устройство'}</div>
                              {k.device.userAgent && (
                                <div className={styles.modalMono}>{k.device.userAgent}</div>
                              )}
                              <div className={styles.deviceMeta}>
                                <span>
                                  активность:{' '}
                                  {k.device.lastActiveAt
                                    ? formatDateTime(k.device.lastActiveAt)
                                    : 'нет данных'}
                                </span>
                                <span>привязано: {formatDateTime(k.device.boundAt)}</span>
                                {k.device.hwidHint && (
                                  <span>
                                    hwid <span className={styles.modalMono}>{k.device.hwidHint}</span>
                                  </span>
                                )}
                              </div>
                              <div className={styles.bindingActions}>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  loading={unbindingKey === k.marzbanUsername}
                                  disabled={unbindingKey !== null}
                                  onClick={() => handleUnbindDevice(selectedUser, k)}
                                >
                                  Отвязать устройство
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className={styles.keyMuted}>не привязан</div>
                          )}
                        </div>

                        <div className={styles.deviceSection}>
                          <div className={styles.deviceTitle}>
                            Устройства{k.devicesLoaded ? ` (${k.devices.length})` : ''}
                          </div>
                          {!k.devicesLoaded ? (
                            <div className={styles.keyMuted}>Не удалось получить из панели</div>
                          ) : k.devices.length === 0 ? (
                            <div className={styles.keyMuted}>Нет устройств — клиент не передаёт HWID или ещё не подключался</div>
                          ) : (
                            k.devices.map((d) => (
                              <div key={d.hwid} className={styles.deviceCard}>
                                <div className={styles.deviceName}>
                                  {d.deviceModel || d.platform || 'Неизвестное устройство'}
                                  {d.platform && d.deviceModel && ` · ${d.platform}`}
                                  {d.osVersion && ` ${d.osVersion}`}
                                </div>
                                {d.userAgent && <div className={styles.modalMono}>{d.userAgent}</div>}
                                <div className={styles.deviceMeta}>
                                  <span>Последний раз: {formatDateTime(d.updatedAt)}</span>
                                  <span>Добавлено: {formatDateTime(d.createdAt)}</span>
                                  {d.requestIp && <span>IP: {d.requestIp}</span>}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                size="sm"
                loading={actionLoading}
                onClick={() => handleToggleRole(selectedUser)}
              >
                {selectedUser.role === 'admin' ? 'Убрать админа' : 'Сделать админом'}
              </Button>
              {selectedUser.isBlocked !== undefined && (
                <Button
                  variant={selectedUser.isBlocked ? 'primary' : 'danger'}
                  size="sm"
                  loading={actionLoading}
                  onClick={() => handleToggleBlock(selectedUser)}
                >
                  {selectedUser.isBlocked ? 'Разблокировать' : 'Заблокировать'}
                </Button>
              )}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
